import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Modal, Spinner } from 'react-bootstrap';
import DataTable from 'react-data-table-component';
import Header_Manager from '../../Components/Header_Manager';
import { CSVLink } from 'react-csv';
import { paybridge_Url, BASE_URL } from '../../Config';

const TABS = {
  SIMPLE: 'simple',
  FULL: 'full',
  SUMMARY: 'summary',
};

const TYPE_FILTER = {
  ALL: 'ALL',
  MWALLET: 'MWALLET',
  TPL: 'TPL',
};

const getRowType = (row) => {
  const hasPayment = !!row.payment_id;
  const hasUrl = !!row.urlGenerate_Id;
  const detailType = String(row.detailType || '').toLowerCase();
  const source = String(row.source || row.apiSource || row.productCode || '').toUpperCase();

  if (hasPayment && !hasUrl) return TYPE_FILTER.MWALLET;
  if (hasUrl && !hasPayment) return TYPE_FILTER.TPL;
  if (detailType === 'payment' || source.includes('MWALLET') || source.includes('JAZZ')) {
    return TYPE_FILTER.MWALLET;
  }
  if (detailType.includes('url') || source.includes('TPL')) {
    return TYPE_FILTER.TPL;
  }
  if (hasPayment) return TYPE_FILTER.MWALLET;
  if (hasUrl) return TYPE_FILTER.TPL;
  return TYPE_FILTER.MWALLET;
};

const Manager_Agent_Sales_Report = () => {
  const [activeTab, setActiveTab] = useState(TABS.SIMPLE);
  const [reportData, setReportData] = useState([]);
  const [summaryBySource, setSummaryBySource] = useState([]);
  const [summaryByProduct, setSummaryByProduct] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [companyId, setCompanyId] = useState('');
  const [companyDetails, setCompanyDetails] = useState([]);
  const [typeFilter, setTypeFilter] = useState(TYPE_FILTER.ALL);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [totalRows, setTotalRows] = useState(0);
  const renderWithTooltip = (value) => {
    const text = value ?? '-';
    return (
      <div
        title={String(text)}
        style={{
          maxWidth: '160px',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {String(text)}
      </div>
    );
  };

  useEffect(() => {
    const user = sessionStorage.getItem('user');
    if (!user) {
      Swal.fire('Error', 'Your session has expired. Please sign in again.', 'error');
      return;
    }

    const userData = JSON.parse(user);

    const mapCompanies = (list) =>
      (Array.isArray(list) ? list : []).map((c) => ({
        applicationUserId: c.companyProfileId || c.applicationUserId,
        name: c.name,
        contactNumber: c.contactNumber,
        productSale: c.productSale,
      }));

    // Prefer session cache first
    if (Array.isArray(userData.assignedCompanies) && userData.assignedCompanies.length > 0) {
      const mapped = mapCompanies(userData.assignedCompanies);
      setCompanyDetails(mapped);
      if (!userData.companyId && mapped[0]?.applicationUserId) {
        setCompanyId(mapped[0].applicationUserId);
      } else {
        setCompanyId(userData.companyId || '');
      }
    }

    if (!userData.userId) return;

    axios
      .get(`${BASE_URL}AssignedCompany?ManagerId=${userData.userId}`)
      .then((response) => {
        if (response.data.responseCode === '0000') {
          const list = Array.isArray(response.data.companyDetails)
            ? response.data.companyDetails
            : [];
          const mapped = mapCompanies(list);
          setCompanyDetails(mapped);

          sessionStorage.setItem(
            'user',
            JSON.stringify({
              ...userData,
              assignedCompanies: list,
            })
          );

          setCompanyId((prev) => prev || mapped[0]?.applicationUserId || '');
        } else {
          setCompanyDetails([]);
          Swal.fire('Info', response.data.message || 'No assigned companies found', 'info');
        }
      })
      .catch(() => {
        setCompanyDetails([]);
        Swal.fire('Error', 'Failed to fetch assigned companies', 'error');
      });
  }, []);

  const filteredData = useMemo(() => {
    if (typeFilter === TYPE_FILTER.ALL) return reportData;
    return reportData.filter((row) => getRowType(row) === typeFilter);
  }, [reportData, typeFilter]);

  const buildListBody = (pageNumber, pageLimit) => {
    const body = {
      page: pageNumber,
      limit: pageLimit,
    };
    if (fromDate) body.startDate = fromDate;
    if (toDate) body.endDate = toDate;
    if (companyId) body.companyId = companyId;
    // optional API source hint
    if (typeFilter === TYPE_FILTER.MWALLET) body.source = 'JAZZCASH_DIRECT';
    if (typeFilter === TYPE_FILTER.TPL) body.source = 'TPL_JC';
    return body;
  };

  const fetchListReport = async (tab = activeTab, pageNumber = page, pageLimit = limit) => {
    if (!companyId) {
      Swal.fire('Error', 'Please select a company.', 'error');
      return;
    }

    setLoading(true);
    setDetail(null);
    try {
      const endpoint =
        tab === TABS.FULL
          ? `${paybridge_Url}report/agent-sales`
          : `${paybridge_Url}report/agent-sales/simple`;

      const response = await axios.post(
        endpoint,
        buildListBody(pageNumber, pageLimit),
        { headers: { 'Content-Type': 'application/json' } }
      );

      if (response.data.success) {
        const rows = Array.isArray(response.data.data) ? response.data.data : [];
        setReportData(rows);
        setTotalRows(response.data.pagination?.totalRecords ?? rows.length);
      } else {
        Swal.fire('Info', response.data.message || 'No data found', 'info');
        setReportData([]);
        setTotalRows(0);
      }
    } catch (error) {
      Swal.fire('Error', error.response?.data?.message || 'Error fetching report', 'error');
      setReportData([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    if (!companyId) {
      Swal.fire('Error', 'Please select a company.', 'error');
      return;
    }

    setLoading(true);
    setDetail(null);
    try {
      const body = { companyId };
      if (fromDate) body.startDate = fromDate;
      if (toDate) body.endDate = toDate;

      const response = await axios.post(
        `${paybridge_Url}report/agent-sales/summary`,
        body,
        { headers: { 'Content-Type': 'application/json' } }
      );

      if (response.data.success) {
        const payload = response.data.data;
        // New shape: { bySource: [], byProductSource: [] }
        // Old shape fallback: []
        if (Array.isArray(payload)) {
          setSummaryBySource(payload);
          setSummaryByProduct([]);
        } else {
          setSummaryBySource(Array.isArray(payload?.bySource) ? payload.bySource : []);
          setSummaryByProduct(Array.isArray(payload?.byProductSource) ? payload.byProductSource : []);
        }
      } else {
        Swal.fire('Info', response.data.message || 'No summary found', 'info');
        setSummaryBySource([]);
        setSummaryByProduct([]);
      }
    } catch (error) {
      Swal.fire('Error', error.response?.data?.message || 'Error fetching summary', 'error');
      setSummaryBySource([]);
      setSummaryByProduct([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFetch = () => {
    setPage(1);
    if (activeTab === TABS.SUMMARY) {
      fetchSummary();
    } else {
      fetchListReport(activeTab, 1, limit);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setReportData([]);
    setSummaryBySource([]);
    setSummaryByProduct([]);
    setDetail(null);
    setPage(1);
    setTotalRows(0);
  };

  const handleTypeFilterChange = (value) => {
    setTypeFilter(value);
    setPage(1);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    fetchListReport(activeTab, newPage, limit);
  };

  const handlePerRowsChange = (newLimit, newPage) => {
    setLimit(newLimit);
    setPage(newPage);
    fetchListReport(activeTab, newPage, newLimit);
  };

  const closeDetailModal = () => {
    setShowDetailModal(false);
    setDetail(null);
    setDetailLoading(false);
  };

  const openDetail = async (row) => {
    setShowDetailModal(true);
    setDetailLoading(true);
    setDetail(null);
    try {
      let url = '';
      if (row.sale_id) {
        url = `${paybridge_Url}report/agent-sales/${row.sale_id}/detail`;
      } else if (getRowType(row) === TYPE_FILTER.MWALLET && (row.payment_id || row.detailId)) {
        url = `${paybridge_Url}report/agent-sales/payment/${row.detailId || row.payment_id}`;
      } else if (getRowType(row) === TYPE_FILTER.TPL && (row.urlGenerate_Id || row.detailId)) {
        url = `${paybridge_Url}report/agent-sales/url-generate/${row.detailId || row.urlGenerate_Id}`;
      } else {
        setShowDetailModal(false);
        Swal.fire('Info', 'No detail id found for this row.', 'info');
        setDetailLoading(false);
        return;
      }

      const response = await axios.get(url);
      if (response.data.success) {
        setDetail(response.data);
      } else {
        setShowDetailModal(false);
        Swal.fire('Info', response.data.message || 'Detail not found', 'info');
      }
    } catch (error) {
      setShowDetailModal(false);
      Swal.fire('Error', error.response?.data?.message || 'Failed to load detail', 'error');
    } finally {
      setDetailLoading(false);
    }
  };

  const simpleColumns = [
    { name: 'Sale ID', selector: (row) => renderWithTooltip(row.sale_id), sortable: true, width: '90px' },
    { name: 'Type', selector: (row) => renderWithTooltip(getRowType(row)), sortable: true, width: '100px' },
    { name: 'Payment ID', selector: (row) => renderWithTooltip(row.payment_id), sortable: true, width: '110px' },
    { name: 'URL Gen ID', selector: (row) => renderWithTooltip(row.urlGenerate_Id), sortable: true, width: '110px' },
    { name: 'agent_Name', selector: (row) => renderWithTooltip(row.agent_Name), sortable: true },
    { name: 'company_Name', selector: (row) => renderWithTooltip(row.company_Name), sortable: true },
    { name: 'Source', selector: (row) => renderWithTooltip(row.source), sortable: true },
    {
      name: 'Date',
      selector: (row) =>
        renderWithTooltip(row.sale_datetime ? new Date(row.sale_datetime).toLocaleString() : '-'),
      sortable: true,
    },
    {
      name: 'Action',
      cell: (row) => (
        <button className="btn btn-sm btn-outline-primary" onClick={() => openDetail(row)}>
          Detail
        </button>
      ),
      ignoreRowClick: true,
      width: '100px',
    },
  ];

  const fullColumns = [
    { name: 'Sale ID', selector: (row) => renderWithTooltip(row.sale_id), sortable: true, width: '90px' },
    { name: 'Type', selector: (row) => renderWithTooltip(getRowType(row)), sortable: true, width: '100px' },
    { name: 'Txn Ref', selector: (row) => renderWithTooltip(row.txn_ref_no), sortable: true },
    { name: 'Amount', selector: (row) => renderWithTooltip(row.amount != null ? `PKR ${row.amount}` : '-'), sortable: true },
    { name: 'Mobile', selector: (row) => renderWithTooltip(row.msisdn ?? row.mobile_no), sortable: true },
    { name: 'Status', selector: (row) => renderWithTooltip(row.status), sortable: true },
    { name: 'Source', selector: (row) => renderWithTooltip(row.source), sortable: true },
    { name: 'Customer', selector: (row) => renderWithTooltip(row.customerName ?? row.CustomerName), sortable: true },
    { name: 'agent_Name', selector: (row) => renderWithTooltip(row.agent_Name), sortable: true },
    { name: 'company_Name', selector: (row) => renderWithTooltip(row.company_Name), sortable: true },
    { name: 'Product', selector: (row) => renderWithTooltip(row.productCode), sortable: true },
    { name: 'Payment URL', selector: (row) => renderWithTooltip(row.paymentUrl), sortable: true },
    {
      name: 'Date',
      selector: (row) =>
        renderWithTooltip(
          (row.createdAt || row.sale_datetime)
            ? new Date(row.createdAt || row.sale_datetime).toLocaleString()
            : '-'
        ),
      sortable: true,
    },
    {
      name: 'Action',
      cell: (row) => (
        <button className="btn btn-sm btn-outline-primary" onClick={() => openDetail(row)}>
          Detail
        </button>
      ),
      ignoreRowClick: true,
      width: '100px',
    },
  ];

  const summarySourceColumns = [
    { name: 'Source', selector: (row) => renderWithTooltip(row.source), sortable: true },
    { name: 'Total', selector: (row) => renderWithTooltip(row.total_count), sortable: true },
    { name: 'Success', selector: (row) => renderWithTooltip(row.success_count), sortable: true },
    { name: 'Success Amount', selector: (row) => renderWithTooltip(row.success_amount != null ? `PKR ${row.success_amount}` : '-'), sortable: true },
    { name: 'Pending', selector: (row) => renderWithTooltip(row.pending_count), sortable: true },
    { name: 'Failed', selector: (row) => renderWithTooltip(row.failed_count), sortable: true },
    { name: 'Initiated', selector: (row) => renderWithTooltip(row.initiated_count), sortable: true },
    { name: 'Agents', selector: (row) => renderWithTooltip(row.distinct_agents), sortable: true },
    { name: 'Companies', selector: (row) => renderWithTooltip(row.distinct_companies), sortable: true },
    { name: 'Total Amount', selector: (row) => renderWithTooltip(row.total_amount != null ? `PKR ${row.total_amount}` : '-'), sortable: true },
  ];

  const summaryProductColumns = [
    { name: 'Product Source', selector: (row) => renderWithTooltip(row.productSource), sortable: true },
    { name: 'Source', selector: (row) => renderWithTooltip(row.source), sortable: true },
    { name: 'Total', selector: (row) => renderWithTooltip(row.total_count), sortable: true },
    { name: 'Success', selector: (row) => renderWithTooltip(row.success_count), sortable: true },
    { name: 'Success Amount', selector: (row) => renderWithTooltip(row.success_amount != null ? `PKR ${row.success_amount}` : '-'), sortable: true },
    { name: 'Pending', selector: (row) => renderWithTooltip(row.pending_count), sortable: true },
    { name: 'Failed', selector: (row) => renderWithTooltip(row.failed_count), sortable: true },
    { name: 'Initiated', selector: (row) => renderWithTooltip(row.initiated_count), sortable: true },
    { name: 'Agents', selector: (row) => renderWithTooltip(row.distinct_agents), sortable: true },
    { name: 'Companies', selector: (row) => renderWithTooltip(row.distinct_companies), sortable: true },
    { name: 'Total Amount', selector: (row) => renderWithTooltip(row.total_amount != null ? `PKR ${row.total_amount}` : '-'), sortable: true },
  ];

  const totalSummary = useMemo(
    () => summaryBySource.find((row) => String(row.source).toUpperCase() === 'TOTAL') || null,
    [summaryBySource]
  );

  const prepareCsvData = () => {
    if (activeTab === TABS.SUMMARY) {
      const bySourceCsv = summaryBySource.map((row) => ({
        Section: 'bySource',
        productSource: '-',
        Source: row.source ?? '-',
        Total: row.total_count ?? '-',
        Success: row.success_count ?? '-',
        'Success Amount': row.success_amount ?? '-',
        Pending: row.pending_count ?? '-',
        Failed: row.failed_count ?? '-',
        Initiated: row.initiated_count ?? '-',
        Agents: row.distinct_agents ?? '-',
        Companies: row.distinct_companies ?? '-',
        'Total Amount': row.total_amount ?? '-',
      }));
      const byProductCsv = summaryByProduct.map((row) => ({
        Section: 'byProductSource',
        productSource: row.productSource ?? '-',
        Source: row.source ?? '-',
        Total: row.total_count ?? '-',
        Success: row.success_count ?? '-',
        'Success Amount': row.success_amount ?? '-',
        Pending: row.pending_count ?? '-',
        Failed: row.failed_count ?? '-',
        Initiated: row.initiated_count ?? '-',
        Agents: row.distinct_agents ?? '-',
        Companies: row.distinct_companies ?? '-',
        'Total Amount': row.total_amount ?? '-',
      }));
      return [...bySourceCsv, ...byProductCsv];
    }

    return filteredData.map((row) => ({
      Type: getRowType(row),
      'Sale ID': row.sale_id ?? '-',
      'Payment ID': row.payment_id ?? '-',
      'URL Gen ID': row.urlGenerate_Id ?? '-',
      'Txn Ref': row.txn_ref_no ?? '-',
      Amount: row.amount ?? '-',
      Mobile: row.msisdn ?? row.mobile_no ?? '-',
      Status: row.status ?? '-',
      Source: row.source ?? '-',
      Customer: row.customerName ?? row.CustomerName ?? '-',
      agent_Name: row.agent_Name ?? '-',
      company_Name: row.company_Name ?? '-',
      Product: row.productCode ?? '-',
      'Payment URL': row.paymentUrl ?? '-',
      Date: row.createdAt ?? row.sale_datetime ?? '-',
    }));
  };

  const DetailField = ({ label, value }) => (
    <div className="col-md-4 mb-2">
      <small className="text-muted d-block">{label}</small>
      <div className="fw-semibold" style={{ wordBreak: 'break-word' }}>
        {value != null && value !== '' ? String(value) : '-'}
      </div>
    </div>
  );

  const detailData = detail?.data || {};
  const sale = detail?.sale || {};
  const detailSource = String(detail?.source || detail?.table || detailData?.apiSource || '').toUpperCase();
  const isJazzDetail =
    detail?.table === 'Jazz_payments' ||
    detailSource.includes('JAZZ') ||
    detailSource.includes('MWALLET') ||
    (!!detailData.txn_ref_no && !detailData.paymentUrl);
  const isUrlDetail =
    detail?.table?.toLowerCase?.().includes('url') ||
    detailSource.includes('TPL') ||
    detailSource.includes('URL') ||
    !!detailData.paymentUrl ||
    !!sale.urlGenerate_Id;

  return (
    <div className="container">
      <Header_Manager />

      <div className="card mt-2">
        <div className="card-header text-center">
          <h2>Agent Sales Report</h2>
        </div>

        <div className="card-body">
          <ul className="nav nav-tabs mb-3">
            <li className="nav-item">
              <button
                type="button"
                className={`nav-link ${activeTab === TABS.SIMPLE ? 'active' : ''}`}
                onClick={() => handleTabChange(TABS.SIMPLE)}
              >
                Simple Report
              </button>
            </li>
            <li className="nav-item">
              <button
                type="button"
                className={`nav-link ${activeTab === TABS.FULL ? 'active' : ''}`}
                onClick={() => handleTabChange(TABS.FULL)}
              >
                Full Report
              </button>
            </li>
            <li className="nav-item">
              <button
                type="button"
                className={`nav-link ${activeTab === TABS.SUMMARY ? 'active' : ''}`}
                onClick={() => handleTabChange(TABS.SUMMARY)}
              >
                Summary
              </button>
            </li>
          </ul>

          <div className="row mb-3 g-2">
            <div className="col-md-2">
              <label>From Date</label>
              <input
                type="date"
                className="form-control"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </div>
            <div className="col-md-2">
              <label>To Date</label>
              <input
                type="date"
                className="form-control"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </div>
            <div className="col-md-2">
              <label>Type</label>
              <select
                className="form-control"
                value={typeFilter}
                onChange={(e) => handleTypeFilterChange(e.target.value)}
              >
                <option value={TYPE_FILTER.ALL}>All</option>
                <option value={TYPE_FILTER.MWALLET}>MWALLET (Jazz Payment)</option>
                <option value={TYPE_FILTER.TPL}>TPL / URL Generate</option>
              </select>
            </div>

            <div className="col-md-3">
              <label>Company</label>
              <select
                className="form-control"
                value={companyId}
                onChange={(e) => setCompanyId(e.target.value)}
              >
                <option value="">Select Company</option>
                {companyDetails.map((c) => (
                  <option key={c.applicationUserId} value={c.applicationUserId}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-2 d-flex align-items-end">
              <button className="btn btn-primary w-100" onClick={handleFetch} disabled={loading}>
                {loading ? 'Loading...' : 'Fetch Report'}
              </button>
            </div>
          </div>

          {activeTab === TABS.SUMMARY ? (
            <>
              {totalSummary && (
                <div className="row g-2 mb-3">
                  <div className="col-md-2">
                    <div className="border rounded p-2 text-center">
                      <small className="text-muted d-block">Total</small>
                      <strong>{totalSummary.total_count ?? 0}</strong>
                    </div>
                  </div>
                  <div className="col-md-2">
                    <div className="border rounded p-2 text-center">
                      <small className="text-muted d-block">Success</small>
                      <strong className="text-success">{totalSummary.success_count ?? 0}</strong>
                    </div>
                  </div>
                  <div className="col-md-2">
                    <div className="border rounded p-2 text-center">
                      <small className="text-muted d-block">Pending</small>
                      <strong className="text-warning">{totalSummary.pending_count ?? 0}</strong>
                    </div>
                  </div>
                  <div className="col-md-2">
                    <div className="border rounded p-2 text-center">
                      <small className="text-muted d-block">Failed</small>
                      <strong className="text-danger">{totalSummary.failed_count ?? 0}</strong>
                    </div>
                  </div>
                  <div className="col-md-2">
                    <div className="border rounded p-2 text-center">
                      <small className="text-muted d-block">Success Amount</small>
                      <strong>PKR {totalSummary.success_amount ?? 0}</strong>
                    </div>
                  </div>
                  <div className="col-md-2">
                    <div className="border rounded p-2 text-center">
                      <small className="text-muted d-block">Total Amount</small>
                      <strong>PKR {totalSummary.total_amount ?? 0}</strong>
                    </div>
                  </div>
                </div>
              )}

              <h6 className="mt-2">By Source</h6>
              <DataTable
                columns={summarySourceColumns}
                data={summaryBySource}
                progressPending={loading}
                pagination
                highlightOnHover
                responsive
                striped
                noDataComponent="No source summary found"
              />

              <h6 className="mt-4">By Product Source</h6>
              <DataTable
                columns={summaryProductColumns}
                data={summaryByProduct}
                progressPending={loading}
                pagination
                highlightOnHover
                responsive
                striped
                noDataComponent="No product summary found"
              />
            </>
          ) : (
            <DataTable
              columns={activeTab === TABS.FULL ? fullColumns : simpleColumns}
              data={filteredData}
              progressPending={loading}
              pagination
              paginationServer
              paginationTotalRows={totalRows}
              paginationDefaultPage={page}
              paginationPerPage={limit}
              onChangePage={handlePageChange}
              onChangeRowsPerPage={handlePerRowsChange}
              highlightOnHover
              responsive
              striped
            />
          )}

          {(filteredData.length > 0 || summaryBySource.length > 0 || summaryByProduct.length > 0) && (
            <CSVLink
              data={prepareCsvData()}
              filename={`agent_sales_${activeTab}_report.csv`}
              className="btn btn-success mt-3"
            >
              Download CSV
            </CSVLink>
          )}

          <Modal show={showDetailModal} onHide={closeDetailModal} size="lg" centered scrollable>
            <Modal.Header closeButton>
              <Modal.Title>
                {detailLoading
                  ? 'Loading Detail...'
                  : isJazzDetail
                    ? 'Jazz Payment Detail (MWALLET)'
                    : isUrlDetail
                      ? 'TPL / URL Generate Detail'
                      : 'Sale Detail'}
                {!detailLoading && detail?.source ? ` — ${detail.source}` : ''}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {detailLoading && (
                <div className="text-center py-4">
                  <Spinner animation="border" size="sm" className="me-2" />
                  Loading detail...
                </div>
              )}

              {!detailLoading && detail && (
                <>
                  {detail.sale && (
                    <>
                      <h6 className="mb-2">Sale</h6>
                      <div className="row mb-3">
                        <DetailField label="Sale ID" value={sale.id} />
                        <DetailField label="Payment ID" value={sale.payment_id} />
                        <DetailField label="URL Generate ID" value={sale.urlGenerate_Id} />
                        <DetailField label="Agent" value={sale.agent_Name} />
                        <DetailField label="Company" value={sale.company_Name} />
                        <DetailField
                          label="Date"
                          value={sale.datetime ? new Date(sale.datetime).toLocaleString() : '-'}
                        />
                      </div>
                      <hr />
                    </>
                  )}

                  {isJazzDetail && (
                    <>
                      <h6 className="mb-2">Jazz Payment (MWALLET)</h6>
                      <div className="row">
                        <DetailField label="Payment ID" value={detailData.id} />
                        <DetailField label="Txn Ref" value={detailData.txn_ref_no} />
                        <DetailField label="Bill Reference" value={detailData.bill_reference} />
                        <DetailField label="Amount" value={detailData.amount} />
                        <DetailField label="Mobile" value={detailData.mobile_no ?? detailData.msisdn} />
                        <DetailField label="Status" value={detailData.status} />
                        <DetailField label="Customer" value={detailData.CustomerName ?? detailData.customerName} />
                        <DetailField label="Product Code" value={detailData.productCode || 'MWALLET'} />
                        <DetailField label="Response Code" value={detailData.response_code ?? detailData.pp_ResponseCode} />
                        <DetailField
                          label="Response Message"
                          value={detailData.response_message ?? detailData.Static_Response}
                        />
                        <DetailField
                          label="Created At"
                          value={
                            detailData.created_at || detailData.createdAt
                              ? new Date(detailData.created_at || detailData.createdAt).toLocaleString()
                              : '-'
                          }
                        />
                        <DetailField label="Remarks" value={detailData.Remarks ?? detailData.remarks} />
                      </div>
                    </>
                  )}

                  {isUrlDetail && !isJazzDetail && (
                    <>
                      <h6 className="mb-2">TPL / URL Generate</h6>
                      <div className="row">
                        <DetailField label="URL Generate ID" value={detailData.id ?? sale.urlGenerate_Id} />
                        <DetailField label="Amount" value={detailData.amount} />
                        <DetailField label="Mobile" value={detailData.msisdn ?? detailData.mobile_no} />
                        <DetailField label="Status" value={detailData.status} />
                        <DetailField label="Payment URL" value={detailData.paymentUrl} />
                        <DetailField label="Provider" value={detailData.provider} />
                        <DetailField label="Customer" value={detailData.CustomerName ?? detailData.customerName} />
                        <DetailField label="Product Code" value={detailData.productCode} />
                        <DetailField
                          label="Created At"
                          value={
                            detailData.created_at || detailData.createdAt
                              ? new Date(detailData.created_at || detailData.createdAt).toLocaleString()
                              : '-'
                          }
                        />
                        <DetailField label="Remarks" value={detailData.Remarks ?? detailData.remarks} />
                      </div>
                    </>
                  )}

                  {!isJazzDetail && !isUrlDetail && (
                    <div className="row">
                      <DetailField label="ID" value={detailData.id} />
                      <DetailField label="Amount" value={detailData.amount} />
                      <DetailField label="Mobile" value={detailData.mobile_no ?? detailData.msisdn} />
                      <DetailField label="Status" value={detailData.status} />
                    </div>
                  )}
                </>
              )}
            </Modal.Body>
            <Modal.Footer>
              <button className="btn btn-secondary" onClick={closeDetailModal}>
                Close
              </button>
            </Modal.Footer>
          </Modal>
        </div>
      </div>
    </div>
  );
};

export default Manager_Agent_Sales_Report;
